import { Component, Inject, OnInit, Optional } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { SrFollowUpComment, SrFollowUpCommentParams, SrFollowUpGet, SrFollowUpService } from '@app/services/sr-follow-up.service';

@Component({
    selector: 'tt-sr-follow-up',
    templateUrl: './sr-follow-up.component.html',
    styleUrls: ['./sr-follow-up.component.css'],
    standalone: false,
})
export class SrFollowUpComponent implements OnInit {
    public page?: SrFollowUpGet;
    public responsibleLabours: Item[] = [];
    public comments: SrFollowUpComment[] = [];
    public adding = false;
    public closing = false;
    public reopening = false;
    public error = '';

    constructor(private readonly followUp: SrFollowUpService, private readonly modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async ngOnInit(): Promise<void> {
        try { await this.loadPage(); } catch (error) { this.showError(error); }
    }

    private async loadPage(): Promise<void> {
        this.page = await this.followUp.getFollowUp({ experience_keyno: String(this.navigator?.params['experience_keyno'] ?? '') });
        await Promise.all([this.loadResponsibleLabours(), this.loadComments()]);
    }

    private async loadResponsibleLabours(): Promise<void> {
        if (!this.page?.special_resource_id_to?.trim()) return;
        this.responsibleLabours = [];
        this.responsibleLabours = await this.followUp.getResponsibleLabours({ special_resource_id_to: this.page.special_resource_id_to });
    }

    private async loadComments(): Promise<void> {
        if (!this.page?.experience_keyno?.trim()) return;
        this.comments = [];
        this.comments = await this.followUp.getComments({ experience_keyno: this.page.experience_keyno });
    }

    public async onResponsibleLabourChanged(value: string): Promise<void> {
        if (!this.page) return;
        this.page.responsible_labour_no = value;
        if (!this.page.experience_keyno?.trim() || !value?.trim()) return;
        try {
            const response = await this.followUp.addResponsibleLabour({ experience_keyno: this.page.experience_keyno, responsible_labour_no: value });
            if (response.errorcode !== '0') await this.showWarning(response.errormessage);
            else await this.loadComments();
        } catch (error) { this.showError(error); }
    }

    public async saveNote(action: SrFollowUpCommentParams['func']): Promise<void> {
        const busy = action === 'ADD' ? 'adding' : action === 'CLOSE' ? 'closing' : 'reopening';
        if (!this.page || this[busy]) return;
        this[busy] = true;
        this.error = '';
        try {
            const response = await this.followUp.addComment({ experience_keyno: this.page.experience_keyno, new_note: this.page.new_note, func: action });
            if (response.errorcode !== '0') await this.showWarning(response.errormessage);
            else {
                this[busy] = false;
                this.page.new_note = '';
                // CLOSE/REOPEN reload both the page and comments, as in the legacy flow.
                if (action === 'ADD') await this.loadComments();
                else await Promise.all([this.loadPage(), this.loadComments()]);
            }
        } catch (error) { this.showError(error); }
        finally { this[busy] = false; }
    }

    public openForward(): void {
        if (this.page) this.navigator?.go({ to: 'srfollowupforward-test', parms: { experience_keyno: this.page.experience_keyno } });
    }
    public openOperation(): void {
        if (this.page) this.navigator?.go({ to: 'sroperation', parms: { custact_no: this.page.custact_no } });
    }
    private showWarning(message: string): Promise<void> {
        return this.modal.show({ type: 'warning', title: 'Varsel', message, buttons: [{ label: 'OK', cssClass: 'btn-warning', close: true }] });
    }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}
